/**
 * Canonical approval envelope detection and result extraction for conversation rendering.
 * Mirrors {@code ApprovalEnvelope} on the backend — keyed by {@code approvalId} in tool output.
 */

import {
  isCreatedFileTool,
  isDynamicFormTool,
} from "../conversationUtils";

export function extractApprovalEnvelope(output) {
  let parsed = output;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  if (
    parsed &&
    typeof parsed === "object" &&
    typeof parsed.approvalId === "string" &&
    parsed.approvalId.length > 0
  ) {
    return parsed;
  }
  return null;
}

export function formatToolResultValue(output) {
  if (output === undefined || output === null) return null;
  return typeof output === "string" ? output : JSON.stringify(output, null, 2);
}

export function extractExecutedToolResult(approvalEnvelope) {
  if (!approvalEnvelope || approvalEnvelope.status !== "APPROVED") return null;
  if (approvalEnvelope.executedResult == null) return null;
  return formatToolResultValue(approvalEnvelope.executedResult);
}

/** Tool parts that must render even when raw tool output is hidden in chat settings. */
const ALWAYS_VISIBLE_HITL_TOOLS = new Set([
  "request_decision",
  "spawn_worker",
  "review_task",
  "ask_human",
  "request_credential_setup",
  "guide_human",
  "create_artifact",
]);

export function isAlwaysVisibleToolPart(part, showToolCallsAndOutputsInChat) {
  if (extractApprovalEnvelope(part?.output)) {
    return true;
  }
  if (showToolCallsAndOutputsInChat) {
    return true;
  }
  const toolName = part?.toolName || "";
  if (isDynamicFormTool(toolName) || isCreatedFileTool(toolName)) {
    return true;
  }
  return ALWAYS_VISIBLE_HITL_TOOLS.has(toolName);
}

export function shouldHideToolPartBySchedulePolicy(
  toolName,
  showSchedulingActivityInChat
) {
  if (showSchedulingActivityInChat) return false;
  return toolName === "schedule_message" || toolName === "recurring_schedule";
}

export function shouldHideToolPartByInterAgentPolicy(
  toolName,
  showInterAgentMessagesInChat
) {
  if (showInterAgentMessagesInChat) return false;
  return toolName === "send_message";
}
